import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import Map, { Marker, NavigationControl, ScaleControl, Source, Layer } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { MapPin, Maximize2, Minimize2, Sparkles } from 'lucide-react';
import { useToast } from '../ui/use-toast';
// NEW: Supabase client for edge function invocation
import { supabase } from '../../integrations/supabase/client';

const MAPBOX_TOKEN = 'pk.eyJ1IjoidmFtbWFuIiwiYSI6ImNtOTNsNW05bzBvbzMyd29qb2g1a2NkOGMifQ.3pdZdM6_IVUetJEGw9uzoQ';
const MAPLE_GIS_PENDING_PLACE_STORAGE_KEY = 'mapleos.maplegis.pendingPlace';

const styles = {
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  satelliteStreets: 'mapbox://styles/mapbox/satellite-streets-v12',
  streets: 'mapbox://styles/mapbox/streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11'
} as const;

type StyleKey = keyof typeof styles;

const MapleGIS: React.FC = () => {
  const mapRef = useRef<MapRef>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const [styleKey, setStyleKey] = useState<StyleKey>('satelliteStreets');
  const [terrainEnabled, setTerrainEnabled] = useState(true);
  const [pitch, setPitch] = useState(45);
  const [bearing, setBearing] = useState(0);
  const [zoom, setZoom] = useState(2.5);
  const [query, setQuery] = useState('');
  const [marker, setMarker] = useState<{ lon: number; lat: number } | null>(null);
  // NEW: track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  // NEW: adjustable radius (km) for "near me" searches
  const [radiusKm, setRadiusKm] = useState(150);

  // AI BAR STATE
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiAnswer, setAiAnswer] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [poiResults, setPoiResults] = useState<Array<{ lon: number; lat: number; name: string }>>([]);
  const [routeGeoJSON, setRouteGeoJSON] = useState<any>(null);
  const [routeEndpoints, setRouteEndpoints] = useState<{ start?: { lon: number; lat: number }, end?: { lon: number; lat: number } }>({});
  // Eye Candy state
  const [buildingsOn, setBuildingsOn] = useState(false);
  const [heatmapData, setHeatmapData] = useState<any>(null);
  const [orbiting, setOrbiting] = useState(false);
  const orbitFrameRef = useRef<number | null>(null);
  const orbitStartTimeRef = useRef<number>(0);

  // NEW: cursor HUD and dropped pins
  const [cursorLngLat, setCursorLngLat] = useState<{ lon: number; lat: number } | null>(null);
  const [pinMode, setPinMode] = useState(false);
  const [droppedPins, setDroppedPins] = useState<Array<{ lon: number; lat: number }>>([]);

  // NEW: toggle fullscreen on the root container
  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
      toast({ title: 'Entering full screen' });
    } else {
      document.exitFullscreen?.();
      toast({ title: 'Exiting full screen' });
    }
  }, [toast]);

  // NEW: listen for fullscreenchange to keep UI state in sync
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      // Ensure map resizes to new dimensions
      mapRef.current?.getMap()?.resize();
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const mapStyle = useMemo(() => styles[styleKey], [styleKey]);

  // Helpers: Geocode a place name to [lon, lat]
  const geocodePlace = useCallback(async (place: string) => {
    const resp = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(place)}.json?access_token=${MAPBOX_TOKEN}`);
    if (!resp.ok) throw new Error('Geocoding failed');
    const json = await resp.json();
    const feature = json.features?.[0];
    if (!feature?.center) throw new Error('No geocoding result');
    return { lon: feature.center[0], lat: feature.center[1], name: feature.place_name || place };
  }, []);

  // UPDATED: Geocode a name constrained to Canada, prefer education POIs, then fallback
  const geocodeNameInCanada = useCallback(async (name: string) => {
    const base = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(name)}.json?country=CA&limit=1&access_token=${MAPBOX_TOKEN}`;

    // Try education-related categories first (college/university/school)
    const urlWithCategories = `${base}&types=poi&categories=education,college,university,school`;
    let resp = await fetch(urlWithCategories);
    if (resp.ok) {
      const json = await resp.json();
      const f = json.features?.[0];
      if (f?.center && typeof f.center[0] === 'number' && typeof f.center[1] === 'number') {
        return { lon: f.center[0], lat: f.center[1], name };
      }
    }

    // Fallback: general POI/place search
    const urlFallback = `${base}&types=poi,place`;
    resp = await fetch(urlFallback);
    if (!resp.ok) return null;
    const json = await resp.json();
    const f = json.features?.[0];
    if (f?.center && typeof f.center[0] === 'number' && typeof f.center[1] === 'number') {
      return { lon: f.center[0], lat: f.center[1], name };
    }
    return null;
  }, []);

  // UPDATED: Ask AI for Canadian places of any type and return clean names
  const askAIForLocations = useCallback(async (topic: string) => {
    const systemPrompt =
      "You are a helpful assistant. Return ONLY a JSON array of official Canadian place names (strings). " +
      "This can include businesses, malls, landmarks, facilities, attractions, schools, restaurants, airports, etc. " +
      "No prose, no markdown, just JSON.";
    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `List up to 50 ${topic} in Canada using official names recognized on maps. Return ONLY a JSON array of names.` }
    ];

    const { data, error } = await supabase.functions.invoke('openai-proxy', {
      body: {
        endpoint: 'chat/completions',
        body: {
          model: 'gpt-4o',
          messages,
          temperature: 0,
          max_tokens: 800
        }
      }
    });

    if (error) {
      throw new Error(error.message || 'AI request failed');
    }

    let raw = (data?.choices?.[0]?.message?.content ?? '') as string;
    if (typeof raw !== 'string') raw = JSON.stringify(raw);

    let names: string[] = [];
    try {
      names = JSON.parse(raw);
    } catch {
      const match = raw.match(/\[[\s\S]*\]/);
      if (match) {
        names = JSON.parse(match[0] || '[]');
      } else {
        names = raw
          .split('\n')
          .map((s) => s.replace(/^[\-\*\d\.\)\s]+/, '').trim())
          .filter(Boolean);
      }
    }

    names = Array.from(new Set((names || []).map((n) => String(n).trim()).filter(Boolean))).slice(0, 50);
    if (names.length === 0) throw new Error('AI did not return any names');
    return names;
  }, []);

  // ADD: Simple Canada intent detector to support Canada-wide queries
  const inCanadaSimple = (text: string): boolean => {
    const t = (text || '').toLowerCase();
    // Canada, provinces/territories, and major cities
    const keywords = [
      'canada','canadian',
      'alberta','british columbia','bc','manitoba','new brunswick','nb','newfoundland','labrador',
      'nova scotia','ns','prince edward island','pei','ontario','quebec','saskatchewan',
      'yukon','northwest territories','nunavut',
      'toronto','vancouver','montreal','calgary','edmonton','ottawa','winnipeg','halifax','victoria',
      'regina','saskatoon','quebec city','st john\'s'
    ];
    return keywords.some(k => t.includes(k));
  };

  // NEW: Fallback search - query Mapbox POIs around major Canadian cities
  const CANADIAN_CITIES = [
    'Toronto, ON',
    'Vancouver, BC',
    'Montreal, QC',
    'Calgary, AB',
    'Edmonton, AB',
    'Ottawa, ON',
    'Winnipeg, MB',
    'Quebec City, QC',
    'Halifax, NS',
    'Victoria, BC',
  ];
  const dedupePOIs = (items: Array<{ lon: number; lat: number; name: string }>) => {
    const seen = new Set<string>();
    const out: Array<{ lon: number; lat: number; name: string }> = [];
    for (const it of items) {
      const k = `${it.name.toLowerCase()}|${it.lon.toFixed(4)}|${it.lat.toFixed(4)}`;
      if (!seen.has(k)) {
        seen.add(k);
        out.push(it);
      }
    }
    return out;
  };
  const searchPOIsAcrossCanada = useCallback(async (topic: string, perCity = 8) => {
    const results: Array<{ lon: number; lat: number; name: string }> = [];
    const cityCenters = await Promise.allSettled(CANADIAN_CITIES.map((c) => geocodePlace(c)));
    const centers = cityCenters
      .filter((s) => s.status === 'fulfilled')
      .map((s: any) => s.value as { lon: number; lat: number; name: string });

    const poiSettled = await Promise.allSettled(
      centers.map((c) => searchPOIsNear(topic, c.lon, c.lat, perCity))
    );

    for (const p of poiSettled) {
      if (p.status === 'fulfilled' && Array.isArray(p.value)) {
        results.push(...p.value);
      }
    }

    return dedupePOIs(results).slice(0, 50);
  }, [geocodePlace]);

  // Helper: parse city/province/country from a Mapbox place_name
  const parseRegionFromPlaceName = (place: string): { city?: string; province?: string; country?: string; label: string } => {
    const parts = (place || '').split(',').map(p => p.trim());
    const country = parts.find(p => /canada/i.test(p)) || undefined;
    // Try to find province or province abbreviation
    const provinceMap: Record<string, string> = {
      'ab': 'Alberta', 'bc': 'British Columbia', 'mb': 'Manitoba', 'nb': 'New Brunswick',
      'nl': 'Newfoundland and Labrador', 'ns': 'Nova Scotia', 'nt': 'Northwest Territories',
      'nu': 'Nunavut', 'on': 'Ontario', 'pe': 'Prince Edward Island', 'qc': 'Quebec',
      'sk': 'Saskatchewan', 'yt': 'Yukon'
    };
    let province: string | undefined;
    for (const p of parts) {
      const norm = p.toLowerCase();
      // Full name
      if (Object.values(provinceMap).some(name => name.toLowerCase() === norm)) {
        province = p;
        break;
      }
      // Abbrev
      const abbr = provinceMap[norm.toLowerCase()];
      if (abbr) {
        province = abbr;
        break;
      }
      // Common city,province patterns like "Vancouver BC"
      const abbrMatch = norm.match(/\b([a-z]{2})\b$/);
      if (abbrMatch && provinceMap[abbrMatch[1]]) {
        province = provinceMap[abbrMatch[1]];
        break;
      }
    }
    // City is often first element if not the province
    const city = parts.length ? parts[0] : undefined;

    const label = province || city || (country ? 'Canada' : (parts[parts.length - 1] || 'Canada'));
    return { city, province, country, label };
  };

  // Helper: extract business names from SerpApi-like results
  const extractNamesFromSearchResults = (results: any, limit = 30): string[] => {
    const names: string[] = [];
    const org = results?.organic_results || [];
    for (const r of org) {
      let title: string = String(r.title || '').trim();
      if (!title) continue;
      // Heuristics: remove common suffixes like " - Yelp", " | Google Maps"
      title = title.replace(/\s+[-|–]\s+(yelp|google\s*maps|mapquest|facebook|tripadvisor|pages)/i, '');
      // Also strip city appended with dash or pipe
      title = title.replace(/\s+[-|–|]\s+[A-Za-z\s]+$/, '');
      // Avoid generic pages
      if (title.length > 2 && !/^(best|top|\d+)/i.test(title)) {
        names.push(title);
      }
      if (names.length >= limit) break;
    }
    // Deduplicate
    const uniq = Array.from(new Set(names.map(n => n.trim()))).filter(Boolean);
    return uniq;
  };

  // Helpers: Search POIs near a location
  const searchPOIsNear = useCallback(async (query: string, nearLon: number, nearLat: number, limit = 10) => {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?types=poi&proximity=${nearLon},${nearLat}&limit=${limit}&access_token=${MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('POI search failed');
    const json = await resp.json();
    const pois = (json.features || []).map((f: any) => ({
      lon: f.center?.[0],
      lat: f.center?.[1],
      name: f.text || f.place_name || query
    })).filter((p: any) => typeof p.lon === 'number' && typeof p.lat === 'number');
    return pois;
  }, []);

  // Helpers: Directions route (driving)
  const getDrivingRoute = useCallback(async (start: { lon: number, lat: number }, end: { lon: number, lat: number }) => {
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lon},${start.lat};${end.lon},${end.lat}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('Directions failed');
    const json = await resp.json();
    const route = json.routes?.[0]?.geometry;
    if (!route) throw new Error('No route found');
    return {
      type: 'Feature',
      properties: {},
      geometry: route
    };
  }, []);

  // Helpers: Reverse geocode current center
  const reverseGeocodeCenter = useCallback(async () => {
    const map = mapRef.current?.getMap();
    if (!map) throw new Error('Map not ready');
    const center = map.getCenter();
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${center.lng},${center.lat}.json?types=place,locality,address&access_token=${MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('Reverse geocode failed');
    const json = await resp.json();
    const feature = json.features?.[0];
    return feature?.place_name || 'Unknown location';
  }, []);

  // Helpers: get user's current location (fallback to map center)
  const getUserLocation = useCallback(async (): Promise<{ lon: number; lat: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const map = mapRef.current?.getMap();
        if (map) {
          const c = map.getCenter();
          resolve({ lon: c.lng, lat: c.lat });
        } else {
          reject(new Error('Geolocation unsupported and map not ready'));
        }
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lon: pos.coords.longitude, lat: pos.coords.latitude }),
        () => {
          const map = mapRef.current?.getMap();
          if (map) {
            const c = map.getCenter();
            resolve({ lon: c.lng, lat: c.lat });
          } else {
            reject(new Error('Could not get your location'));
          }
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
      );
    });
  }, []);

  // Helpers: distance filter (Haversine)
  const haversineKm = (lon1: number, lat1: number, lon2: number, lat2: number) => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Helpers: compute bbox string for Mapbox geocoding
  const computeBBox = (lon: number, lat: number, radiusKm: number) => {
    const deltaLat = radiusKm / 111; // ~km per degree latitude
    const deltaLon = radiusKm / (111 * Math.max(0.1, Math.cos((lat * Math.PI) / 180))); // avoid division by zero near poles
    const minLon = Math.max(-180, lon - deltaLon);
    const maxLon = Math.min(180, lon + deltaLon);
    const minLat = Math.max(-90, lat - deltaLat);
    const maxLat = Math.min(90, lat + deltaLat);
    return { minLon, minLat, maxLon, maxLat };
  };

  // NEW: search within bbox then filter by radius, fallback to proximity sampling
  const searchPOIsInBBox = useCallback(async (query: string, lon: number, lat: number, radius: number, limit = 10) => {
    const { minLon, minLat, maxLon, maxLat } = computeBBox(lon, lat, radius);
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?types=poi&bbox=${minLon},${minLat},${maxLon},${maxLat}&limit=${limit}&access_token=${MAPBOX_TOKEN}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error('POI bbox search failed');
    const json = await resp.json();
    const raw = (json.features || []).map((f: any) => ({
      lon: f.center?.[0],
      lat: f.center?.[1],
      name: f.text || f.place_name || query
    })).filter((p: any) => typeof p.lon === 'number' && typeof p.lat === 'number');
    return raw.filter((p: any) => haversineKm(lon, lat, p.lon, p.lat) <= radius);
  }, []);

  // NOTE: searchPOIsNear is defined earlier; we reuse it here
  const searchPOIsWithinRadius = useCallback(async (query: string, lon: number, lat: number, radius: number) => {
    // First try bbox-limited search
    let results = await searchPOIsInBBox(query, lon, lat, radius, 10);

    // If too few, sample around the circle using proximity to get more
    if (results.length < 8) {
      const samples: Array<{ lon: number; lat: number }> = [];
      const { minLon, minLat, maxLon, maxLat } = computeBBox(lon, lat, radius);
      // 8 directions around center + corners
      const angles = [0, 45, 90, 135, 180, 225, 270, 315];
      const radialLon = (radius / (111 * Math.max(0.1, Math.cos((lat * Math.PI) / 180))));
      const radialLat = radius / 111;
      for (const a of angles) {
        const rad = (a * Math.PI) / 180;
        samples.push({
          lon: lon + radialLon * Math.cos(rad),
          lat: lat + radialLat * Math.sin(rad)
        });
      }
      // Also add bbox corners
      samples.push({ lon: minLon, lat: minLat }, { lon: maxLon, lat: minLat }, { lon: minLon, lat: maxLat }, { lon: maxLon, lat: maxLat });

      const settled = await Promise.allSettled(samples.map((s) => searchPOIsNear(query, s.lon, s.lat, 6)));
      for (const p of settled) {
        if (p.status === 'fulfilled' && Array.isArray(p.value)) {
          results.push(...p.value);
        }
      }
      // Filter to radius and dedupe
      results = dedupePOIs(results).filter((p) => haversineKm(lon, lat, p.lon, p.lat) <= radius).slice(0, 50);
    }
    return results;
  }, [searchPOIsInBBox, searchPOIsNear]);

  // Helper: pick a reasonable zoom for a km radius
  const zoomForRadius = (r: number) => {
    if (r <= 25) return 12;
    if (r <= 50) return 10.5;
    if (r <= 100) return 9;
    if (r <= 150) return 8;
    return 7.5;
  };

  // Define flyTo before handleAIPrompt so it's initialized when used in deps
  const flyTo = useCallback((lon: number, lat: number, z: number = 8) => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    setMarker({ lon, lat });
    map.flyTo({
      center: [lon, lat],
      zoom: z,
      speed: 0.8,
      curve: 1.4,
      bearing,
      pitch,
      essential: true
    });
  }, [bearing, pitch]);

  // Define handleSearch before handleAIPrompt to avoid TDZ with its dep
  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;

    try {
      const resp = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}`
      );
      if (!resp.ok) throw new Error('Failed to geocode');
      const json = await resp.json();
      const feature = json.features?.[0];
      if (feature?.center) {
        const [lon, lat] = feature.center;
        flyTo(lon, lat, Math.max(6, zoom));
      } else {
        toast({ title: 'No results', description: 'Try a different place or spelling.' });
      }
    } catch (e) {
      toast({ title: 'Search failed', description: 'Unable to find that location right now.' });
    }
  }, [query, flyTo, toast, zoom]);

  useEffect(() => {
    const focusPlace = async (place: string) => {
      const normalizedPlace = String(place || '').trim();
      if (!normalizedPlace) {
        return;
      }

      setQuery(normalizedPlace);
      setAiPrompt(`fly to ${normalizedPlace}`);
      setAiAnswer(`Opening ${normalizedPlace} in MapleGIS…`);

      try {
        const loc = await geocodePlace(normalizedPlace);
        setMarker({ lon: loc.lon, lat: loc.lat });
        setRouteGeoJSON(null);
        setRouteEndpoints({});
        setPoiResults([]);
        flyTo(loc.lon, loc.lat, Math.max(8, zoom));
        setAiAnswer(`Showing ${loc.name}.`);
      } catch {
        setAiAnswer(`I couldn't locate ${normalizedPlace} on the map.`);
        toast({ title: 'Map lookup failed', description: `Unable to find ${normalizedPlace} right now.` });
      }
    };

    const usePendingPlace = () => {
      try {
        const pendingPlace = localStorage.getItem(MAPLE_GIS_PENDING_PLACE_STORAGE_KEY);
        if (!pendingPlace) {
          return;
        }
        localStorage.removeItem(MAPLE_GIS_PENDING_PLACE_STORAGE_KEY);
        void focusPlace(pendingPlace);
      } catch {
      }
    };

    const handleFocusPlace = (event: Event) => {
      const detail = (event as CustomEvent<{ place?: string }>).detail;
      if (!detail?.place) {
        return;
      }
      try {
        localStorage.removeItem(MAPLE_GIS_PENDING_PLACE_STORAGE_KEY);
      } catch {
      }
      void focusPlace(detail.place);
    };

    usePendingPlace();
    window.addEventListener('maplegis:focus-place', handleFocusPlace as EventListener);
    return () => window.removeEventListener('maplegis:focus-place', handleFocusPlace as EventListener);
  }, [flyTo, geocodePlace, toast, zoom]);

  // AI command handler (rule-based to keep it fast and offline)
  const handleAIPrompt = useCallback(async () => {
    const input = aiPrompt.trim();
    if (!input) return;
    setIsProcessing(true);
    setAiAnswer('');
    try {
      const text = input.toLowerCase();

      // Eye Candy: 3D Buildings in Vancouver
      if (/(3d\s+buildings|buildings\s+3d|enable\s+3d\s+buildings)/.test(text) || /3d\s+buildings.*vancouver|vancouver.*3d\s+buildings/.test(text)) {
        await enableBuildingsDemo();
        setAiAnswer('3D buildings enabled in Vancouver with a cinematic orbit.');
        return;
      }

      // Eye Candy: Rockies Flyover (Banff terrain exaggeration + orbit)
      if (/rockies\s+flyover|banff\s+flyover|flyover\s+banff/.test(text)) {
        await rockiesFlyover();
        setAiAnswer('Rockies flyover initiated over Banff with amplified terrain.');
        return;
      }

      // Eye Candy: Pet Stores Heatmap (Vancouver)
      if (/heatmap.*pet|pet.*heatmap/.test(text) || /pet\s+stores\s+heatmap|vancouver\s+heatmap.*pet/.test(text)) {
        await showPetStoresHeatmap();
        setAiAnswer('Pet-care heatmap rendered for Vancouver with glowing density.');
        return;
      }

      // Eye Candy: Orbit Camera (optional speed like "orbit camera at 12")
      const orbitStartMatch = text.match(/orbit\s+(?:camera)?(?:\s+at\s+(\d+))?/);
      if (orbitStartMatch && !/stop\s+orbit|end\s+orbit|disable\s+orbit/.test(text)) {
        const speed = orbitStartMatch[1] ? parseInt(orbitStartMatch[1], 10) : 10;
        startOrbit(Number.isFinite(speed) ? speed : 10, 60);
        setAiAnswer(`Orbit camera started${orbitStartMatch[1] ? ` at ${speed}°/s` : ''}.`);
        return;
      }

      // Eye Candy: Stop Orbit
      if (/stop\s+orbit|end\s+orbit|disable\s+orbit/.test(text)) {
        stopOrbit();
        setAiAnswer('Orbit camera stopped.');
        return;
      }

      // Eye Candy: Clear demos
      if (/clear\s+(?:demos|overlays|effects)/.test(text)) {
        setBuildingsOn(false);
        setHeatmapData(null);
        stopOrbit();
        toast({ title: 'Eye candy cleared' });
        setAiAnswer('Cleared demo layers and animations.');
        return;
      }

      // NEW: "find me X near me" using adjustable radius and geolocation
      const nearMeMatch = text.match(/(find|search)\s+(?:me\s+)?(.+?)\s+near\s+me/);
      if (nearMeMatch) {
        const topic = nearMeMatch[2].trim();
        toast({ title: 'Locating you…', description: `Searching ${topic} within ${radiusKm} km` });
        const me = await getUserLocation();
        const results = await searchPOIsWithinRadius(topic, me.lon, me.lat, radiusKm);
        setPoiResults(results);
        setMarker(null);
        setRouteGeoJSON(null);
        setRouteEndpoints({});
        flyTo(me.lon, me.lat, zoomForRadius(radiusKm));
        setAiAnswer(`Found ${results.length} ${topic} within ${radiusKm} km of your location.`);
        toast({ title: 'Done', description: `Plotted ${results.length} places near you.` });
        return;
      }

      // AI-driven bulk search: "find me all X in canada" or "list X in canada" or simple "X in canada"
      const canadaListMatch = text.match(/(?:find(?:\s+me)?\s+all|list)\s+(.+?)\s+in\s+canada/);
      const canadaSimpleMatch = canadaListMatch ? null : text.match(/^(.+?)\s+in\s+canada$/);
      const findAllCanadaMatch = canadaListMatch || canadaSimpleMatch;
      if (findAllCanadaMatch) {
        const rawGroup = findAllCanadaMatch[1];
        const topic = (typeof rawGroup === 'string' && rawGroup.trim().length > 0)
          ? rawGroup.trim()
          : (findAllCanadaMatch[0] || '').replace(/\s+in\s+canada$/, '').trim();
        if (!topic) {
          // If we still fail to derive a topic, bail gracefully
          setAiAnswer('Please specify what to find in Canada (e.g., "pet stores in Canada").');
          return;
        }
        toast({ title: 'Asking AI', description: `Gathering ${topic} in Canada…` });

        // 1) Try AI list of names
        let names: string[] = [];
        let results: Array<{ lon: number; lat: number; name: string }> = [];
        let skipped = 0;

        try {
          names = await askAIForLocations(topic);
          const settled = await Promise.allSettled(names.map((name) => geocodeNameInCanada(name)));
          results = settled
            .filter((s) => s.status === 'fulfilled' && s.value)
            .map((s: any) => s.value as { lon: number; lat: number; name: string });
          skipped = names.length - results.length;
        } catch {
          // ignore AI failure and rely on fallback
        }

        // 2) Fallback: If AI produced few results, search Mapbox POIs across major cities
        if (results.length < 5) {
          const poiFallback = await searchPOIsAcrossCanada(topic, 10);
          results = dedupePOIs([...(results || []), ...poiFallback]);
        }

        if (results.length === 0) {
          setPoiResults([]);
          setAiAnswer(`No locations found for ${topic} in Canada.`);
          return;
        }

        setPoiResults(results);
        setMarker(null);
        setRouteGeoJSON(null);
        setRouteEndpoints({});

        const avgLon = results.reduce((a, r) => a + r.lon, 0) / results.length;
        const avgLat = results.reduce((a, r) => a + r.lat, 0) / results.length;
        flyTo(avgLon, avgLat, Math.max(3.5, 4));

        setAiAnswer(`Plotted ${results.length} ${topic} in Canada. ${skipped > 0 ? `Skipped ${skipped} not found.` : ''}`);
        toast({ title: 'Done', description: `Plotted ${results.length} locations.` });
        return;
      }

      // Route: "route from X to Y"
      const routeMatch = text.match(/route\s+from\s+(.+?)\s+to\s+(.+)/);
      if (routeMatch) {
        const fromPlace = routeMatch[1].trim();
        const toPlace = routeMatch[2].trim();
        const start = await geocodePlace(fromPlace);
        const end = await geocodePlace(toPlace);
        const route = await getDrivingRoute(start, end);
        setRouteGeoJSON(route);
        setRouteEndpoints({ start, end });
        setPoiResults([]);
        setMarker(null);
        flyTo(start.lon, start.lat, Math.max(8, zoom));
        setAiAnswer(`Showing driving route from ${fromPlace} to ${toPlace}.`);
        return;
      }

      // Fly: "fly to X" / "go to X" / "navigate to X"
      const flyMatch = text.match(/(fly|go|navigate)\s+to\s+(.+)/);
      if (flyMatch) {
        const place = flyMatch[2].trim();
        const loc = await geocodePlace(place);
        setMarker({ lon: loc.lon, lat: loc.lat });
        setRouteGeoJSON(null);
        setRouteEndpoints({});
        setPoiResults([]);
        flyTo(loc.lon, loc.lat, Math.max(6, zoom));
        setAiAnswer(`Flying to ${loc.name}.`);
        return;
      }

      // Search POIs near: "find coffee near banff"
      const findNearMatch = text.match(/(find|search)\s+(.+?)\s+near\s+(.+)/);
      if (findNearMatch) {
        const queryWord = findNearMatch[2].trim();
        const nearPlace = findNearMatch[3].trim();
        const nearLoc = await geocodePlace(nearPlace);
        const pois = await searchPOIsNear(queryWord, nearLoc.lon, nearLoc.lat);
        setPoiResults(pois);
        setMarker(null);
        setRouteGeoJSON(null);
        setRouteEndpoints({});
        flyTo(nearLoc.lon, nearLoc.lat, Math.max(11, zoom));
        setAiAnswer(`Found ${pois.length} ${queryWord} places near ${nearLoc.name}.`);
        return;
      }

      // Search POIs around current center: "find coffee" or "find me coffee"
      const findMatch = text.match(/(find|search)\s+(?:me\s+)?(.+)/);
      if (findMatch) {
        const queryWord = findMatch[2].trim();
        const map = mapRef.current?.getMap();
        if (!map) throw new Error('Map not ready');
        const c = map.getCenter();

        // Step 1: infer region from center
        const centerName = await reverseGeocodeCenter();
        const region = parseRegionFromPlaceName(centerName);
        const searchQuery = `${queryWord} in ${region.label}`;

        // Step 2: web-search businesses in region
        let plotted: Array<{ lon: number; lat: number; name: string }> = [];
        try {
          const { data, error } = await supabase.functions.invoke('web-search', { body: { query: searchQuery } });
          if (error) {
            throw new Error(error.message || 'Web search failed');
          }
          const names = extractNamesFromSearchResults(data.results, 40);
          if (names.length === 0) {
            // Fallback to radius-based POI search if web results are sparse
            const pois = await searchPOIsWithinRadius(queryWord, c.lng, c.lat, radiusKm);
            plotted = pois;
          } else {
            // Step 3: geocode each name within Canada or region
            const settled = await Promise.allSettled(
              names.map(async (nm) => {
                // Prefer Canada geocode helper; if it fails, try with region label
                const byCanada = await geocodeNameInCanada(nm);
                if (byCanada) return byCanada;
                const byRegion = await geocodePlace(`${nm}, ${region.label}`);
                return byRegion;
              })
            );
            const points = settled
              .filter(s => s.status === 'fulfilled' && s.value && typeof (s as any).value.lon === 'number' && typeof (s as any).value.lat === 'number')
              .map((s: any, index: number) => ({
                lon: s.value.lon,
                lat: s.value.lat,
                name: s.value.name || names[index] || queryWord,
              }));

            plotted = dedupePOIs(points).slice(0, 50);
          }
        } catch (err) {
          // On any failure, fallback to radius-based search around center
          const pois = await searchPOIsWithinRadius(queryWord, c.lng, c.lat, radiusKm);
          plotted = pois;
        }

        // Step 4: plot results
        setPoiResults(plotted);
        setMarker(null);
        setRouteGeoJSON(null);
        setRouteEndpoints({});
        flyTo(c.lng, c.lat, zoomForRadius(radiusKm));
        setAiAnswer(`Found ${plotted.length} ${queryWord} in ${region.label}.`);
        toast({ title: 'Done', description: `Plotted ${plotted.length} places in ${region.label}.` });
        return;
      }

      // What's here
      if (text.includes("what's here") || text.includes('whats here') || text.includes('where am i')) {
        const name = await reverseGeocodeCenter();
        setAiAnswer(`You're looking at: ${name}`);
        return;
      }

      // Style: "style dark", "style satellite streets"
      const styleMatch = text.match(/style\s+(satellite streets|satellite|streets|dark|light)/);
      if (styleMatch) {
        const style = styleMatch[1].replace(/\s+/g, '');
        setStyleKey(style as StyleKey);
        setAiAnswer(`Style set to ${styleMatch[1]}.`);
        return;
      }

      // Terrain: "terrain on/off"
      const terrainMatch = text.match(/terrain\s+(on|off)/);
      if (terrainMatch) {
        const on = terrainMatch[1] === 'on';
        setTerrainEnabled(on);
        setAiAnswer(`Terrain 3D ${on ? 'enabled' : 'disabled'}.`);
        return;
      }

      // Zoom: "set zoom to 12"
      const zoomMatch = text.match(/set\s+zoom\s+to\s+(\d+(\.\d+)?)/);
      if (zoomMatch) {
        const z = parseFloat(zoomMatch[1]);
        setZoom(z);
        mapRef.current?.getMap()?.setZoom(z);
        setAiAnswer(`Zoom set to ${z}.`);
        return;
      }

      // Pitch: "set pitch to 45"
      const pitchMatch = text.match(/set\s+pitch\s+to\s+(\d+)/);
      if (pitchMatch) {
        const p = parseInt(pitchMatch[1], 10);
        setPitch(p);
        mapRef.current?.getMap()?.setPitch(p);
        setAiAnswer(`Pitch set to ${p}°.`);
        return;
      }

      // Bearing: "set bearing to 180"
      const bearingMatch = text.match(/set\s+bearing\s+to\s+(\d+)/);
      if (bearingMatch) {
        const b = parseInt(bearingMatch[1], 10);
        setBearing(b);
        mapRef.current?.getMap()?.setBearing(b);
        setAiAnswer(`Bearing set to ${b}°.`);
        return;
      }

      // Fallback: use existing search
      setQuery(input);
      await handleSearch();
      setAiAnswer(`Searched for "${input}".`);
    } catch (err: any) {
      toast({ title: 'AI action failed', description: err?.message || 'Something went wrong' });
      setAiAnswer("Sorry, I couldn't complete that request.");
    } finally {
      setIsProcessing(false);
    }
  }, [aiPrompt, getUserLocation, searchPOIsWithinRadius, radiusKm, flyTo, toast, zoom, geocodePlace, getDrivingRoute, searchPOIsNear, reverseGeocodeCenter, handleSearch, setQuery, askAIForLocations, geocodeNameInCanada, searchPOIsAcrossCanada]);

  // NEW: update HUD as mouse moves
  const handleMouseMove = useCallback((e: any) => {
    const lng = e?.lngLat?.lng;
    const lat = e?.lngLat?.lat;
    if (typeof lng === 'number' && typeof lat === 'number') {
      setCursorLngLat({ lon: lng, lat });
    }
  }, []);

  // NEW: drop pin on click when Pin Mode is enabled
  const handleMapClick = useCallback((e: any) => {
    if (!pinMode) return;
    const lng = e?.lngLat?.lng;
    const lat = e?.lngLat?.lat;
    if (typeof lng === 'number' && typeof lat === 'number') {
      setDroppedPins((prev) => [...prev, { lon: lng, lat }]);
      toast({ title: 'Pin dropped', description: `${lat.toFixed(5)}, ${lng.toFixed(5)}` });
    }
  }, [pinMode, toast]);

  // NEW: keep UI zoom/pitch/bearing synced with map interactions
  const handleMapMove = useCallback((e: any) => {
    const vs = e?.viewState;
    if (!vs) return;
    if (typeof vs.zoom === 'number') setZoom(vs.zoom);
    if (typeof vs.pitch === 'number') setPitch(vs.pitch);
    if (typeof vs.bearing === 'number') setBearing(vs.bearing);
  }, []);

  const setupGlobe = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // Globe projection + atmosphere
    // Projection is independent of style; safe to set on any style
    // @ts-ignore - Mapbox types may not include setProjection yet
    map.setProjection('globe');

    // Add fog/atmosphere for the globe look
    // @ts-ignore - fog types may be incomplete
    map.setFog({
      range: [0.8, 8],
      color: 'rgba(255,255,255,0.08)',
      'horizon-blend': 0.2,
      'space-color': 'rgb(0,0,0)',
      'star-intensity': 0.0
    });

    // Sky layer provides a nice atmosphere
    if (!map.getLayer('sky')) {
      map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'atmosphere',
          'sky-atmosphere-sun-intensity': 15
        }
      });
    }

    // Terrain setup
    if (!map.getSource('mapbox-dem')) {
      map.addSource('mapbox-dem', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      } as any);
    }
    map.setTerrain(terrainEnabled ? { source: 'mapbox-dem', exaggeration: 1.2 } : undefined);
  }, [terrainEnabled]);

  // Add/remove 3D buildings layer imperatively when enabled
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (buildingsOn) {
      // Ensure we're on a style that has the 'composite' building source
      if (styleKey === 'satellite') {
        setStyleKey('streets');
      }
      // Add layer when style is fully ready
      const addLayer = () => {
        if (!map.getLayer('3d-buildings')) {
          map.addLayer({
            id: '3d-buildings',
            source: 'composite',
            'source-layer': 'building',
            // Robust filter: Polygon geometry with a 'height' attribute
            filter: ['all', ['==', ['geometry-type'], 'Polygon'], ['has', 'height']],
            type: 'fill-extrusion',
            minzoom: 14,
            paint: {
              'fill-extrusion-color': [
                'interpolate',
                ['linear'], ['get', 'height'],
                0, '#A0AEC0',
                50, '#7DD3FC',
                150, '#60A5FA',
                300, '#4F46E5'
              ],
              'fill-extrusion-height': ['get', 'height'],
              'fill-extrusion-base': ['get', 'min_height'],
              'fill-extrusion-opacity': 0.9
            }
          } as any);
        }
      };
      if (map.isStyleLoaded()) addLayer();
      else map.once('idle', addLayer);
    } else {
      if (map.getLayer('3d-buildings')) {
        map.removeLayer('3d-buildings');
      }
    }
  }, [buildingsOn, styleKey]);

  // Orbit camera animation
  const startOrbit = useCallback((speedDegPerSec = 12, pitchDeg = 60) => {
    const map = mapRef.current?.getMap();
    if (!map || orbiting) return;
    setOrbiting(true);
    orbitStartTimeRef.current = performance.now();
    const loop = () => {
      if (!map || !orbiting) return;
      const t = (performance.now() - orbitStartTimeRef.current) / 1000;
      const bearingNow = (bearing + (t * speedDegPerSec)) % 360;
      map.easeTo({ bearing: bearingNow, pitch: pitchDeg, duration: 500, easing: (n: number) => n });
      orbitFrameRef.current = requestAnimationFrame(loop);
    };
    orbitFrameRef.current = requestAnimationFrame(loop);
  }, [bearing, orbiting]);

  const stopOrbit = useCallback(() => {
    setOrbiting(false);
    if (orbitFrameRef.current) {
      cancelAnimationFrame(orbitFrameRef.current);
      orbitFrameRef.current = null;
    }
  }, []);

  // Demo: enable 3D buildings in Vancouver
  const enableBuildingsDemo = useCallback(async () => {
    toast({ title: '3D Buildings', description: 'Flying to Vancouver and enabling extruded buildings.' });
    const v = await geocodePlace('Vancouver, BC');
    setStyleKey('streets');
    setBuildingsOn(true);
    flyTo(v.lon, v.lat, 15.5);
    setPitch(60);
    setBearing(45);
    startOrbit(10, 65);
  }, [flyTo, geocodePlace, startOrbit, toast]);

  // Demo: Rockies flyover around Banff with terrain exaggeration
  const rockiesFlyover = useCallback(async () => {
    toast({ title: 'Rockies Flyover', description: 'Orbiting over Banff with amplified terrain.' });
    const b = await geocodePlace('Banff National Park');
    const map = mapRef.current?.getMap();
    if (map) {
      // Increase terrain exaggeration temporarily
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 2.0 } as any);
    }
    setStyleKey('satelliteStreets');
    setBuildingsOn(false);
    flyTo(b.lon, b.lat, 11.5);
    setPitch(70);
    startOrbit(6, 70);
  }, [flyTo, geocodePlace, startOrbit, toast]);

  // Demo: Heatmap of pet stores in Vancouver
  const showPetStoresHeatmap = useCallback(async () => {
    toast({ title: 'Heatmap', description: 'Rendering pet stores heatmap in Vancouver.' });
    const v = await geocodePlace('Vancouver, BC');

    // Make colors pop
    setStyleKey('dark');

    // Fly to city center
    flyTo(v.lon, v.lat, 11);

    // Query multiple related categories to ensure ample points
    const queries = [
      'pet store',
      'veterinarian',
      'animal hospital',
      'pet grooming',
      'pet supplies',
      'pet food',
      'pet daycare'
    ];

    const settled = await Promise.allSettled(
      queries.map((q) => searchPOIsInBBox(q, v.lon, v.lat, 35, 80))
    );

    let pois: Array<{ lon: number; lat: number; name: string }> = [];
    for (const p of settled) {
      if (p.status === 'fulfilled' && Array.isArray(p.value)) {
        pois.push(...p.value);
      }
    }

    // If still sparse, try a radius search around Vancouver center as fallback
    if (pois.length < 20) {
      const moreSettled = await Promise.allSettled(
        ['pet store', 'veterinarian', 'pet grooming'].map((q) =>
          searchPOIsWithinRadius(q, v.lon, v.lat, 25)
        )
      );
      for (const m of moreSettled) {
        if (m.status === 'fulfilled' && Array.isArray(m.value)) {
          pois.push(...m.value);
        }
      }
    }

    pois = dedupePOIs(pois);

    // Build GeoJSON for heatmap and circles
    const fc = {
      type: 'FeatureCollection',
      features: pois.map((p) => ({
        type: 'Feature',
        properties: { name: p.name },
        geometry: { type: 'Point', coordinates: [p.lon, p.lat] }
      }))
    };

    setHeatmapData(fc);
    setBuildingsOn(false);
    stopOrbit();

    toast({
      title: 'Heatmap ready',
      description: `Plotted ${pois.length} pet-care locations in Vancouver.`
    });
  }, [geocodePlace, flyTo, searchPOIsInBBox, searchPOIsWithinRadius, stopOrbit]);

  const onMapLoad = useCallback(() => {
    setupGlobe();
  }, [setupGlobe]);

  const onStyleData = useCallback(() => {
    // Re-apply globe/terrain after style changes
    setupGlobe();
  }, [setupGlobe]);

  // NEW: make the Mapbox instance resize when the container changes (e.g., maximize)
  useEffect(() => {
    const resizeMap = () => {
      mapRef.current?.getMap()?.resize();
    };
    // Initial resize after mount
    resizeMap();

    const el = rootRef.current;
    const ro = new ResizeObserver(() => resizeMap());
    if (el) ro.observe(el);

    // Also listen to window resizes
    window.addEventListener('resize', resizeMap);

    return () => {
      window.removeEventListener('resize', resizeMap);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="h-full w-full relative bg-black"
      onContextMenu={(e) => {
        e.preventDefault();      // block browser menu
        e.stopPropagation();     // block Desktop's global right-click menu
      }}
    >
      {/* Controls overlay (left) */}
      <div className="absolute top-3 left-3 z-10 w-[320px]">
        <Card className="bg-black/60 border-white/10 text-white p-3 backdrop-blur-sm space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a place (e.g., Ottawa, Banff)"
              className="bg-white/10 text-white placeholder:text-white/60"
            />
            <Button onClick={handleSearch} variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
              Go
            </Button>
            {/* NEW: Fullscreen toggle */}
            <Button
              onClick={toggleFullscreen}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white"
              aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
              title={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Style</span>
            <Select value={styleKey} onValueChange={(v) => setStyleKey(v as StyleKey)}>
              <SelectTrigger className="w-[180px] bg-white/10 text-white border-white/20">
                <SelectValue placeholder="Select style" />
              </SelectTrigger>
              <SelectContent className="bg-neutral-900 text-white">
                <SelectItem value="satellite">Satellite</SelectItem>
                <SelectItem value="satelliteStreets">Satellite Streets</SelectItem>
                <SelectItem value="streets">Streets</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="light">Light</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm">Terrain (3D)</span>
            <Switch checked={terrainEnabled} onCheckedChange={setTerrainEnabled} />
          </div>

          {/* NEW: Pin Mode toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm">Pin Mode</span>
            <Switch checked={pinMode} onCheckedChange={setPinMode} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Pitch</span>
              <span className="font-mono">{pitch.toFixed(0)}°</span>
            </div>
            <Slider
              value={[pitch]}
              max={60}
              min={0}
              step={1}
              onValueChange={(v) => {
                const val = v[0] ?? 0;
                setPitch(val);
                mapRef.current?.getMap()?.setPitch(val);
              }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Bearing</span>
              <span className="font-mono">{bearing.toFixed(0)}°</span>
            </div>
            <Slider
              value={[bearing]}
              max={360}
              min={0}
              step={1}
              onValueChange={(v) => {
                const val = v[0] ?? 0;
                setBearing(val);
                mapRef.current?.getMap()?.setBearing(val);
              }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Zoom</span>
              <span className="font-mono">{zoom.toFixed(1)}</span>
            </div>
            <Slider
              value={[zoom]}
              max={16}
              min={1}
              step={0.1}
              onValueChange={(v) => {
                const val = v[0] ?? 1;
                setZoom(val);
                mapRef.current?.getMap()?.setZoom(val);
              }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Search radius (km)</span>
              <span className="font-mono">{radiusKm.toFixed(0)} km</span>
            </div>
            <Slider
              value={[radiusKm]}
              max={300}
              min={10}
              step={5}
              onValueChange={(v) => {
                const val = v[0] ?? 150;
                setRadiusKm(val);
              }}
            />
          </div>
        </Card>
      </div>

      {/* AI Bar overlay (right) */}
      <div className="absolute top-3 right-3 z-10 w-[360px]">
        <Card className="bg-black/60 border-white/10 text-white p-3 backdrop-blur-sm space-y-3">
          <div className="flex items-center gap-2">
            <Input
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Ask AI (e.g., route from Toronto to Montreal)"
              className="bg-white/10 text-white placeholder:text-white/60"
            />
            <Button
              onClick={handleAIPrompt}
              disabled={isProcessing}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white"
              aria-label="Ask AI"
              title="Ask AI"
            >
              {isProcessing ? (
                <>
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  <span>Thinking…</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span>Ask</span>
                </>
              )}
            </Button>
          </div>

          {aiAnswer && (
            <div className="text-xs text-white/80">
              {aiAnswer}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setAiPrompt('route from Toronto to Montreal')}
            >
              Route: Toronto → Montreal
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setAiPrompt('find coffee near Banff')}
            >
              Find coffee near Banff
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setAiPrompt('fly to Vancouver')}
            >
              Fly to Vancouver
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => {
                setPoiResults([]);
                setRouteGeoJSON(null);
                setRouteEndpoints({});
                setDroppedPins([]);
                setMarker(null);
                toast({ title: 'Cleared overlays' });
              }}
            >
              Clear overlays
            </Button>
          </div>
        </Card>
      </div>

      {/* NEW: Cursor coordinates HUD (bottom-center) */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
        <div className="px-3 py-1 rounded-full bg-black/50 border border-white/10 backdrop-blur-md text-white text-xs font-mono shadow-lg flex items-center gap-3">
          <span className="opacity-80">Cursor</span>
          <span>
            {cursorLngLat ? `${cursorLngLat.lat.toFixed(5)}, ${cursorLngLat.lon.toFixed(5)}` : '—'}
          </span>
          <span className="opacity-60">•</span>
          <span>Zoom {zoom.toFixed(1)}</span>
        </div>
      </div>

      {/* Map viewport */}
      <div className="absolute inset-0">
        <Map
          ref={mapRef}
          initialViewState={{
            longitude: -95,
            latitude: 55,
            zoom,
            pitch,
            bearing
          }}
          mapStyle={mapStyle}
          mapboxAccessToken={MAPBOX_TOKEN}
          antialias={true}
          onLoad={onMapLoad}
          onStyleData={onStyleData}
          attributionControl={false}
          onMouseMove={handleMouseMove}
          onClick={handleMapClick}
          onMove={handleMapMove}
        >
          <NavigationControl position="bottom-right" />
          <ScaleControl position="bottom-left" />

          {/* Heatmap layer */}
          {heatmapData && (
            <Source id="heat" type="geojson" data={heatmapData}>
              <Layer
                id="heatmap-petstores"
                type="heatmap"
                paint={{
                  // Larger radius for an obvious glow
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    9, 30,
                    12, 50,
                    15, 70
                  ],
                  // Stronger intensity so low densities show
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    9, 1.2,
                    12, 2.0,
                    15, 3.0
                  ],
                  // Lower thresholds so color appears even with sparse points
                  'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0.0, 'rgba(0,0,0,0)',
                    0.05, '#22d3ee',
                    0.10, '#38bdf8',
                    0.20, '#818cf8',
                    0.40, '#a78bfa',
                    0.60, '#f472b6',
                    1.0, '#fb7185'
                  ],
                  'heatmap-opacity': 0.95
                } as any}
              />
              {/* Circle overlay so points are clearly visible */}
              <Layer
                id="heatmap-points"
                type="circle"
                paint={{
                  'circle-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    9, 2,
                    12, 4,
                    15, 6
                  ],
                  'circle-color': '#fbbf24',
                  'circle-stroke-color': '#1f2937',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.85
                } as any}
              />
            </Source>
          )}

          {/* Route line */}
          {routeGeoJSON && (
            <Source id="route" type="geojson" data={routeGeoJSON}>
              <Layer
                id="route-line"
                type="line"
                paint={{
                  'line-color': '#00A2FF',
                  'line-width': 5,
                  'line-opacity': 0.9
                }}
              />
            </Source>
          )}

          {/* Route endpoints */}
          {routeEndpoints.start && (
            <Marker longitude={routeEndpoints.start.lon} latitude={routeEndpoints.start.lat} anchor="bottom">
              <div className="flex items-center justify-center">
                <MapPin className="text-green-500" />
              </div>
            </Marker>
          )}
          {routeEndpoints.end && (
            <Marker longitude={routeEndpoints.end.lon} latitude={routeEndpoints.end.lat} anchor="bottom">
              <div className="flex items-center justify-center">
                <MapPin className="text-blue-500" />
              </div>
            </Marker>
          )}

          {/* POI markers */}
          {poiResults.map((p, i) => (
            <Marker key={`poi-${i}`} longitude={p.lon} latitude={p.lat} anchor="bottom">
              <div className="flex items-center justify-center" title={p.name}>
                <MapPin className="h-4 w-4 text-yellow-400" />
              </div>
            </Marker>
          ))}

          {/* NEW: Dropped pin markers with coordinate labels */}
          {droppedPins.map((p, i) => (
            <Marker key={`drop-${i}`} longitude={p.lon} latitude={p.lat} anchor="bottom">
              <div className="flex flex-col items-center group">
                <MapPin className="h-5 w-5 text-fuchsia-400 drop-shadow-md" />
                <div className="mt-1 px-2 py-1 rounded-md bg-black/60 text-white text-[10px] font-mono backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity">
                  {p.lat.toFixed(5)}, {p.lon.toFixed(5)}
                </div>
              </div>
            </Marker>
          ))}

          {/* Single search marker */}
          {marker && (
            <Marker longitude={marker.lon} latitude={marker.lat} anchor="bottom">
              <div className="flex items-center justify-center">
                <MapPin className="text-red-500" />
              </div>
            </Marker>
          )}
        </Map>
      </div>
    </div>
  );
};

export default MapleGIS;